import { useCallback, useEffect, useRef, useState } from 'react'
import DOMPurify from 'dompurify'
import { Bold, Italic, Link2, RemoveFormatting } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * A small rich text editor for email bodies.
 *
 * WHY NOT A LIBRARY. The CRM has no editor at all today - NoteEditor is a plain textarea
 * whose own placeholder admits "full rich-text editor coming in v2". The order email
 * templates need paragraphs, links, bold and a signature table, and nothing more. Adding
 * tiptap or Quill for that is a large dependency and a new set of conventions for a job
 * this size. If a second feature needs real rich text, replace this with one of them
 * rather than growing it.
 *
 * WHY contenteditable IS SAFE HERE. Every value that arrives is run through DOMPurify
 * before it touches the DOM, and the server sanitises again on send. The browser pass is
 * for the person editing; the server pass is the one that protects the recipient, because
 * a client can always be bypassed.
 *
 * ON execCommand. It is deprecated and has been for years, and it is still the only
 * one-line way to toggle bold on a selection in every browser that matters. The
 * alternative is hand-rolled Range surgery, which is a great deal of code to get subtly
 * wrong. Revisit if a browser actually drops it.
 */

/**
 * Mirrors the server's allowlist in services/emailHtmlSanitiser.js.
 *
 * It has to mirror it closely, and the FIRST version did not. It applied one flat
 * attribute list to every tag with no restriction on style values, while the server uses
 * a per-tag map and a style whitelist. The result was an editor that happily showed
 * things the server then stripped - style on an anchor, width on a table, a class
 * attribute, an h5 - so the preview was not the email. Nothing dangerous, which is
 * exactly why it would have gone unnoticed.
 *
 * The rule to keep: this list must never be MORE permissive than the server's, because
 * anything extra it allows is something the customer will not receive.
 */
const ALLOWED_TAGS = [
  'p',
  'br',
  'div',
  'span',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'ul',
  'ol',
  'li',
  'a',
  'h1',
  'h2',
  'h3',
  'h4',
  'table',
  'thead',
  'tbody',
  'tr',
  'td',
  'th',
  'blockquote',
  'hr',
]

/** Per tag, matching the server. */
const ALLOWED_ATTR_BY_TAG: Record<string, string[]> = {
  a: ['href', 'title'],
  table: ['role', 'cellpadding', 'cellspacing', 'style'],
  td: ['style', 'colspan', 'rowspan'],
  th: ['style', 'colspan', 'rowspan', 'scope'],
  tr: ['style'],
  p: ['style'],
  span: ['style'],
  div: ['style'],
}

/** Style properties the server keeps. Anything else is dropped from a style attribute. */
const ALLOWED_STYLE_PROPS = new Set([
  'color',
  'background-color',
  'font-family',
  'font-size',
  'font-weight',
  'text-align',
  'text-decoration',
  'padding',
  'margin',
  'border-collapse',
  'vertical-align',
])

let hooksInstalled = false

/**
 * Apply the per-tag rules DOMPurify's plain config cannot express.
 *
 * Installed once, globally, which is safe here because it only ever removes attributes
 * the email sanitiser would remove anyway. Guarded so repeated renders do not stack
 * duplicate hooks.
 */
function installHooks() {
  if (hooksInstalled) return
  hooksInstalled = true

  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    const tag = node.nodeName?.toLowerCase()
    const permitted = ALLOWED_ATTR_BY_TAG[tag] ?? []
    if (!permitted.includes(data.attrName)) {
      data.keepAttr = false
      return
    }
    if (data.attrName === 'style') {
      // Keep only the declarations the server keeps, so a style that survives here
      // survives all the way to the customer.
      const kept = String(data.attrValue)
        .split(';')
        .map((d) => d.trim())
        .filter(Boolean)
        .filter((d) => ALLOWED_STYLE_PROPS.has(d.split(':')[0]?.trim().toLowerCase() ?? ''))
      data.attrValue = kept.join(';')
      if (kept.length === 0) data.keepAttr = false
    }
  })
}

export function sanitiseHtml(html: string): string {
  installHooks()
  return DOMPurify.sanitize(html ?? '', {
    ALLOWED_TAGS,
    // The union of the per-tag lists; the hook above narrows it per tag.
    ALLOWED_ATTR: [
      'href',
      'title',
      'style',
      'role',
      'cellpadding',
      'cellspacing',
      'colspan',
      'rowspan',
      'scope',
    ],
    // javascript: and data: are refused; mailto and https are needed by every template.
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  })
}

interface RichTextEditorProps {
  value: string
  onChange: (html: string, text: string) => void
  /** Required: a contenteditable with no name is an unlabelled text field. */
  label: string
  id?: string
  describedById?: string
  disabled?: boolean
  className?: string
}

export function RichTextEditor({
  value,
  onChange,
  label,
  id = 'rich-text-editor',
  describedById,
  disabled = false,
  className,
}: RichTextEditorProps) {
  const ref = useRef<HTMLDivElement>(null)
  const lastEmitted = useRef<string>('')
  const [active, setActive] = useState({ bold: false, italic: false })

  // Write into the DOM only when the incoming value is genuinely different from what is
  // already there. Assigning innerHTML on every render would move the caret to the start
  // on every keystroke, which is the classic contenteditable bug.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const incoming = sanitiseHtml(value)
    if (incoming !== el.innerHTML && incoming !== lastEmitted.current) {
      el.innerHTML = incoming
    }
  }, [value])

  const emit = useCallback(() => {
    const el = ref.current
    if (!el) return
    const html = sanitiseHtml(el.innerHTML)
    lastEmitted.current = html
    // innerText rather than textContent: it respects line breaks, so the plain-text
    // alternative reads like an email instead of one run-together paragraph.
    onChange(html, el.innerText)
  }, [onChange])

  const refreshToolbar = useCallback(() => {
    if (!ref.current || document.activeElement !== ref.current) return
    try {
      setActive({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
      })
    } catch {
      // queryCommandState throws in some browsers when there is no selection. The
      // toolbar simply stays as it was; nothing about the document is wrong.
    }
  }, [])

  useEffect(() => {
    document.addEventListener('selectionchange', refreshToolbar)
    return () => document.removeEventListener('selectionchange', refreshToolbar)
  }, [refreshToolbar])

  const exec = (command: string, argument?: string) => {
    ref.current?.focus()
    document.execCommand(command, false, argument)
    refreshToolbar()
    emit()
  }

  const addLink = () => {
    const selection = window.getSelection()?.toString()
    if (!selection) {
      window.alert('Select the words you want to turn into a link first.')
      return
    }
    const url = window.prompt('Link address', 'https://')
    if (!url) return
    if (!/^(https?:|mailto:)/i.test(url)) {
      window.alert('Links must start with https:// or mailto: to survive being sent.')
      return
    }
    exec('createLink', url)
  }

  const tools = [
    { key: 'bold', icon: Bold, label: 'Bold', run: () => exec('bold'), pressed: active.bold },
    {
      key: 'italic',
      icon: Italic,
      label: 'Italic',
      run: () => exec('italic'),
      pressed: active.italic,
    },
    { key: 'link', icon: Link2, label: 'Add a link', run: addLink },
    {
      key: 'clear',
      icon: RemoveFormatting,
      label: 'Remove formatting',
      run: () => exec('removeFormat'),
    },
  ]

  return (
    <div className={cn('rounded-md border', className)}>
      <div
        role="toolbar"
        aria-label={`${label} formatting`}
        aria-controls={id}
        className="flex items-center gap-1 border-b bg-muted/40 px-1.5 py-1"
      >
        {tools.map((tool) => (
          <Button
            key={tool.key}
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            disabled={disabled}
            // Not aria-pressed on the link and clear buttons: they are actions, not
            // toggles, and announcing a pressed state for them would be a lie.
            aria-pressed={tool.pressed === undefined ? undefined : tool.pressed}
            aria-label={tool.label}
            title={tool.label}
            // Keeps the selection alive: a mousedown on a button would otherwise blur the
            // editor and collapse what the user had chosen to embolden.
            onMouseDown={(e) => e.preventDefault()}
            onClick={tool.run}
          >
            <tool.icon className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        ))}
      </div>

      <div
        ref={ref}
        id={id}
        role="textbox"
        aria-multiline="true"
        aria-label={label}
        aria-describedby={describedById}
        aria-disabled={disabled || undefined}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onKeyUp={refreshToolbar}
        onMouseUp={refreshToolbar}
        // Paste as text, then let the editor's own formatting apply. Pasting from Word or
        // a Google Doc otherwise drags in a mountain of markup that the server strips
        // anyway, so the preview and the sent mail would disagree.
        onPaste={(e) => {
          e.preventDefault()
          const text = e.clipboardData.getData('text/plain')
          document.execCommand('insertText', false, text)
        }}
        className={cn(
          'min-h-[16rem] max-h-[28rem] overflow-y-auto px-3 py-2 text-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          // Email bodies are prose, so give the markup readable defaults. The editor is
          // not trying to reproduce the recipient's mail client.
          '[&_p]:mb-3 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5',
          '[&_a]:underline [&_a]:text-primary [&_table]:mb-3',
          disabled && 'opacity-60'
        )}
      />
    </div>
  )
}
